/**
 * QuotaWarningModal Component
 *
 * Part of Story 3.3: Quota Warning Modal (95%)
 * Epic 3: Frontend UI Components
 *
 * Displays an urgent modal when user reaches 95% of their monthly quota.
 * This is a high-intent conversion trigger encouraging users to upgrade
 * before hitting the hard limit.
 *
 * Acceptance Criteria:
 * - AC#1: Modal appears automatically when usage >= 95%
 * - AC#2: Title: "⚠️ Almost at your limit!"
 * - AC#3: Message with current usage and quota
 * - AC#4: Orange/amber progress bar theme
 * - AC#5: "Upgrade to Agency" CTA button
 * - AC#6: "Remind me later" dismissal (every 3 executions)
 * - AC#7: Fade in + slide up animation
 * - AC#8: Full-screen on mobile (<768px)
 */

import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import { useTierData } from '@/hooks/useTierData';

const DISMISSAL_KEY_PREFIX = 'quota_modal_dismissed_95_';

interface DismissalState {
  executionCount: number;
  dismissedAt: string;
}

/**
 * Get dismissal state from localStorage
 */
function getDismissalState(): DismissalState | null {
  try {
    const stored = localStorage.getItem(`${DISMISSAL_KEY_PREFIX}state`);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

/**
 * Save dismissal state to localStorage
 */
function saveDismissalState(executionCount: number): void {
  const state: DismissalState = {
    executionCount,
    dismissedAt: new Date().toISOString(),
  };
  localStorage.setItem(`${DISMISSAL_KEY_PREFIX}state`, JSON.stringify(state));
}

/**
 * Check if modal should be shown based on usage and dismissal history
 */
function shouldShowModal(usage: number, limit: number, currentUsage: number): boolean {
  const usagePercentage = (currentUsage / limit) * 100;

  // Don't show if usage < 95%
  if (usagePercentage < 95) return false;

  // Check dismissal state
  const dismissal = getDismissalState();

  // Never dismissed - show modal
  if (!dismissal) return true;

  // Calculate executions since last dismissal
  const executionsSinceDismissal = currentUsage - dismissal.executionCount;

  // Show every 3 executions after dismissal
  return executionsSinceDismissal >= 3;
}

/**
 * QuotaWarningModal Component
 *
 * High-intent conversion modal shown at 95% quota usage.
 * Encourages users to upgrade before hitting the hard limit.
 *
 * @example
 * ```tsx
 * // In AppLayout or Dashboard
 * <QuotaWarningModal />
 * ```
 */
export const QuotaWarningModal: React.FC = () => {
  const navigate = useNavigate();
  const { data: tierData, isLoading } = useTierData();
  const [isOpen, setIsOpen] = useState(false);

  /**
   * Check if modal should be shown whenever tier data changes
   */
  useEffect(() => {
    if (isLoading || !tierData || !tierData.quota_limit || isNaN(tierData.usage_percentage)) return;

    const shouldShow = shouldShowModal(
      tierData.usage_percentage,
      tierData.quota_limit,
      tierData.current_usage
    );

    setIsOpen(shouldShow);
  }, [tierData, isLoading]);

  /**
   * Handle "Remind me later" - dismiss for 3 executions
   */
  const handleDismiss = useCallback(() => {
    if (tierData) {
      saveDismissalState(tierData.current_usage);
      setIsOpen(false);
    }
  }, [tierData]);

  /**
   * Handle "Upgrade to Agency" - navigate to pricing page
   */
  const handleUpgrade = useCallback(() => {
    navigate('/pricing');
    setIsOpen(false);
  }, [navigate]);

  /**
   * Manually close modal (X button)
   */
  const handleClose = useCallback(() => {
    handleDismiss();
  }, [handleDismiss]);

  // Don't render if no tier data
  if (!tierData) return null;

  const { current_usage = 0, quota_limit = 0, usage_percentage = 0, tier } = tierData;

  // Determine recommended upgrade tier
  const recommendedTier = tier === 'solo' ? 'Agency Starter' : 'Agency Pro';

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 " />
        </Transition.Child>

        {/* Modal positioning */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Modal panel */}
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-full md:translate-y-4 md:scale-95"
              enterTo="opacity-100 translate-y-0 md:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 md:scale-100"
              leaveTo="opacity-0 translate-y-full md:translate-y-4 md:scale-95"
            >
              <Dialog.Panel
                className="
                  w-full max-w-md transform overflow-hidden rounded-2xl
                  bg-[#0C1319] shadow-2xl shadow-black/50 border border-white/[0.06] transition-all
                  fixed md:relative bottom-0 md:bottom-auto inset-x-0 md:inset-x-auto
                  rounded-b-none md:rounded-b-2xl
                "
              >
                {/* Close button */}
                <button
                  onClick={handleClose}
                  className="
                    absolute top-4 right-4 p-2
                    text-orange-400 hover:text-orange-300
                    hover:bg-orange-500/10 rounded-lg
                    transition-colors
                  "
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5" />
                </button>

                {/* Modal content */}
                <div className="p-6 sm:p-8">
                  {/* Icon and Title */}
                  <div className="flex items-start mb-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                        <AlertTriangle className="w-6 h-6 text-orange-400" />
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <Dialog.Title
                        as="h3"
                        className="text-xl font-bold text-white"
                        id="quota-warning-title"
                      >
                        ⚠️ Almost at your limit!
                      </Dialog.Title>
                    </div>
                  </div>

                  {/* Message */}
                  <Dialog.Description
                    className="text-base text-white/80 mb-6"
                    id="quota-warning-description"
                  >
                    You've used{' '}
                    <span className="font-bold text-orange-400">
                      {current_usage} of {quota_limit}
                    </span>{' '}
                    executions (
                    <span className="font-bold text-orange-400">
                      {Math.round(usage_percentage)}%
                    </span>
                    ). Upgrade now to continue without interruption.
                  </Dialog.Description>

                  {/* Progress Bar - Orange/Amber Theme */}
                  <div className="mb-6">
                    <div className="flex justify-between text-sm text-white/60 mb-2">
                      <span>Quota Usage</span>
                      <span className="font-semibold text-orange-400">
                        {Math.round(usage_percentage)}%
                      </span>
                    </div>
                    <div className="w-full bg-orange-500/20 rounded-full h-3 overflow-hidden">
                      <div
                        className="bg-orange-500 h-full transition-all duration-500 ease-out"
                        style={{ width: `${Math.min(usage_percentage, 100)}%` }}
                        role="progressbar"
                        aria-valuenow={usage_percentage}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`Quota usage: ${Math.round(usage_percentage)}%`}
                      />
                    </div>
                  </div>

                  {/* Upgrade Benefits */}
                  <div className="bg-orange-500/10 border border-orange-400/20 rounded-lg p-4 mb-6">
                    <p className="text-sm font-semibold text-orange-300 mb-2">
                      {recommendedTier} includes:
                    </p>
                    <ul className="text-sm text-orange-200 space-y-1">
                      <li>✓ 500 executions/month (10x more!)</li>
                      <li>✓ More AI agents unlocked</li>
                      <li>✓ Priority support</li>
                    </ul>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    {/* Primary: Upgrade */}
                    <button
                      onClick={handleUpgrade}
                      className="
                        flex-1 px-6 py-3 text-base font-semibold
                        text-white bg-orange-600
                        rounded-lg shadow-sm
                        hover:bg-orange-700
                        focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-[#0C1319]
                        transition-colors duration-200
                        order-1
                      "
                      type="button"
                    >
                      Upgrade to {recommendedTier}
                    </button>

                    {/* Secondary: Dismiss */}
                    <button
                      onClick={handleDismiss}
                      className="
                        flex-1 px-6 py-3 text-base font-semibold
                        text-orange-400 bg-white/[0.04] border-2 border-orange-400/30
                        rounded-lg shadow-sm
                        hover:bg-orange-500/10
                        focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-[#0C1319]
                        transition-colors duration-200
                        order-2
                      "
                      type="button"
                    >
                      Remind me later
                    </button>
                  </div>

                  {/* Dismissal info */}
                  <p className="text-xs text-white/50 text-center mt-4">
                    Will remind you again in 3 executions
                  </p>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

QuotaWarningModal.displayName = 'QuotaWarningModal';
